import { useMemo } from 'react';

import type { QueryKey, UseQueryOptions } from '@tanstack/react-query';

import genericRetriever, {
  type IGenericRetrieverOptions,
} from '@/hooks/requests/genericRetriever';
import useSession from '@/hooks/context/useSession';
import type { NetworkError } from '@/errors/NetworkError';

function useGenericOptions<Response>({
  url,
  queryKey,
  withAuth,
  extraQueryOptions,
  enabled,
  retry,
  ...rest
}: {
  url: string;
  queryKey: QueryKey;
  withAuth: boolean;
  extraQueryOptions?: Partial<IGenericRetrieverOptions>;
  enabled?: boolean;
  retry?: UseQueryOptions<Response, NetworkError, Response, QueryKey>['retry'];
}) {
  const {
    session: { jwt },
  } = useSession();

  return useMemo(() => {
    const requestEnabled = extraQueryOptions?.enabled ?? true;

    return {
      queryKey,
      queryFn: genericRetriever<Response>({
        url,
        jwt: withAuth ? jwt : undefined,
        ...extraQueryOptions,
      }),
      enabled: enabled !== false && requestEnabled,
      // An explicit `retry: undefined` would override the QueryClient default
      // policy and fall back to TanStack's three retries, including on 401.
      ...(retry === undefined ? {} : { retry }),
      ...rest,
    };
  }, [queryKey, url, withAuth, jwt, extraQueryOptions, enabled, rest, retry]);
}

export default useGenericOptions;
